import { useMemo } from 'react';

import dayjs from 'dayjs';

interface DemoGymExpirationData {
  hoursLeft: number;
  daysLeft: number;
  minutesLeft: number;
  progressPercentage: number;
  timeText: string;
  isExpired: boolean;
  color: 'kiloBlue' | 'yellow' | 'red' | 'gray';
}

export const useDemoGymExpiration = (expiresAt: string | null): DemoGymExpirationData => {
  return useMemo(() => {
    if (!expiresAt) {
      return {
        hoursLeft: 0,
        daysLeft: 0,
        minutesLeft: 0,
        progressPercentage: 100,
        timeText: '0h left',
        isExpired: true,
        color: 'gray',
      };
    }

    const now = dayjs();
    const expiration = dayjs(expiresAt);

    const hoursLeft = Math.max(0, expiration.diff(now, 'hour', true));
    const daysLeft = Math.max(0, expiration.diff(now, 'day', true));
    const minutesLeft = Math.max(0, Math.ceil(hoursLeft * 60));

    const hoursLeftRounded = Math.ceil(hoursLeft);
    const daysLeftRounded = Math.ceil(daysLeft);

    const isExpired = hoursLeft <= 0;

    // Calculate progress percentage (48 hours = 100% -> 0 hours = 0%)
    const progressDurationHours = 48;
    const progressDurationMs = progressDurationHours * 60 * 60 * 1000;
    const timeRemaining = expiration.diff(now, 'millisecond');

    const progressPercentage =
      hoursLeftRounded >= progressDurationHours
        ? 100
        : Math.min(100, Math.max(0, (timeRemaining / progressDurationMs) * 100));

    // Determine color based on time left
    let color: 'kiloBlue' | 'yellow' | 'red' | 'gray';
    if (isExpired) {
      color = 'gray';
    } else if (daysLeftRounded <= 1 && hoursLeftRounded > 12) {
      color = 'yellow';
    } else if (hoursLeftRounded <= 12) {
      color = 'red';
    } else {
      color = 'kiloBlue';
    }

    // Generate time text
    let timeText: string;
    if (isExpired) {
      timeText = '0h left';
    } else if (daysLeftRounded > 1) {
      timeText = `${daysLeftRounded}d left`;
    } else if (hoursLeftRounded > 1) {
      timeText = `${hoursLeftRounded}h left`;
    } else if (minutesLeft > 1) {
      timeText = `${Math.ceil(minutesLeft)}m left`;
    } else {
      timeText = '0m left';
    }

    return {
      hoursLeft,
      daysLeft,
      minutesLeft,
      progressPercentage,
      timeText,
      isExpired,
      color,
    };
  }, [expiresAt]);
};
